import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Play, ShoppingBag, Eye, VolumeX, Volume2 } from 'lucide-react';
import Hls from 'hls.js';
import api from '../api/client';
import { getTranslation as t, isRTL } from '../utils/translations';

const VIDEO_AUDIO_PREF_KEY = 'luxvono_video_audio_pref';

const VideoCard = ({ video, layout = 'grid', onAddToCart, onBuyNow, showToast }) => {
    const resolveInitialMuteState = () => {
        if (typeof window === 'undefined') return false;
        const pref = window.localStorage?.getItem(VIDEO_AUDIO_PREF_KEY);
        return pref === 'muted';
    };

    const [visible, setVisible] = useState(false);
    const [isPlaying, setIsPlaying] = useState(false);
    const [isMuted, setIsMuted] = useState(resolveInitialMuteState);
    const [addedToCart, setAddedToCart] = useState(false);
    const containerRef = useRef();
    const videoRef = useRef();
    const hlsRef = useRef(null);

    const getBaseUrl = () => {
        const base = api.defaults.baseURL || process.env.REACT_APP_API_BASE_URL;
        if (base && !base.includes('undefined')) return base;
        if (typeof window !== 'undefined' && window.location.hostname.match(/^[0-9.]+$/)) return `http://${window.location.hostname}:8080`;
        return 'https://api.luxvono.com';
    };

    const rawVideoUrl = (video.videoUrl || video.mediaUrl) || '';
    const videoSrc = rawVideoUrl.startsWith('http')
        ? rawVideoUrl
        : `${getBaseUrl()}${rawVideoUrl.startsWith('/') ? '' : '/'}${rawVideoUrl}`;
    const deriveThumbnailFromVideoUrl = (url) => {
        if (!url) return null;
        const match = url.match(/^\/media\/videos\/([^/]+)\/master\.m3u8$/);
        return match ? `/media/products/${match[1]}_thumb.jpg` : null;
    };
    const derivedThumbnail = deriveThumbnailFromVideoUrl(video.videoUrl || video.mediaUrl);
    const isHlsStream = typeof videoSrc === 'string' && videoSrc.includes('.m3u8');

    useEffect(() => {
        if (!visible || !videoRef.current) return;

        if (Hls.isSupported() && isHlsStream) {
            if (hlsRef.current) hlsRef.current.destroy();
            const hls = new Hls({ startLevel: -1, capLevelToPlayerSize: false, autoStartLoad: true });
            hls.loadSource(videoSrc);
            hls.attachMedia(videoRef.current);
            hlsRef.current = hls;
            hls.on(Hls.Events.MANIFEST_PARSED, () => {
                if (isPlaying) videoRef.current.play().catch(e => console.warn(e));
            });
        } else if (isHlsStream && videoRef.current.canPlayType('application/vnd.apple.mpegurl')) {
            // iOS/Safari native HLS fallback
            videoRef.current.src = videoSrc;
            if (isPlaying) videoRef.current.play().catch(() => setIsPlaying(false));
        }

        return () => {
            if (hlsRef.current) {
                hlsRef.current.destroy();
                hlsRef.current = null;
            }
        };
    }, [visible, videoSrc, isPlaying, isHlsStream]);

    useEffect(() => {
        // Observer for lazy loading the element itself
        const observer = new IntersectionObserver(([entry]) => {
            if (entry.isIntersecting) {
                setVisible(true);
                observer.disconnect();
            }
        }, { rootMargin: '200px' });
        if (containerRef.current) observer.observe(containerRef.current);
        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        if (typeof window === 'undefined') return undefined;

        const promoteAudioOnGesture = () => {
            if (window.globalAudioUnmuted !== true) {
                window.globalAudioUnmuted = true;
                window.localStorage?.setItem(VIDEO_AUDIO_PREF_KEY, 'unmuted');
                if (videoRef.current && visible) {
                    videoRef.current.muted = false;
                    setIsMuted(false);
                }
            }
        };

        window.addEventListener('pointerdown', promoteAudioOnGesture, { once: true });
        return () => window.removeEventListener('pointerdown', promoteAudioOnGesture);
    }, [visible]);

    useEffect(() => {
        if (!visible || !videoRef.current) return;

        // Observer for Auto-Play / Pause based on viewport visibility
        const playObserver = new IntersectionObserver(([entry]) => {
            if (!videoRef.current) return;
            if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
                if (typeof window !== 'undefined' && typeof window.globalAudioUnmuted === 'undefined') {
                    window.globalAudioUnmuted = !resolveInitialMuteState();
                }

                videoRef.current.muted = !(window.globalAudioUnmuted === true);
                setIsMuted(videoRef.current.muted);

                videoRef.current.play().then(() => setIsPlaying(true)).catch(() => {
                    if (!videoRef.current) return;
                    videoRef.current.muted = true;
                    setIsMuted(true);
                    if (typeof window !== 'undefined') {
                        window.globalAudioUnmuted = false;
                        window.localStorage?.setItem(VIDEO_AUDIO_PREF_KEY, 'muted');
                    }
                    videoRef.current.play().then(() => setIsPlaying(true)).catch(() => setIsPlaying(false));
                });
            } else {
                videoRef.current.pause();
                setIsPlaying(false);
            }
        }, { threshold: 0.5 });

        playObserver.observe(videoRef.current);
        return () => playObserver.disconnect();
    }, [visible]);

    const togglePlay = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (videoRef.current.paused) {
            setIsPlaying(true);
            videoRef.current.play().catch(err => {
                if (err.name !== 'AbortError') setIsPlaying(false);
            });
        } else {
            setIsPlaying(false);
            videoRef.current.pause();
        }
    };

    const toggleMute = (e) => {
        e?.stopPropagation();
        const newState = !isMuted;
        setIsMuted(newState);
        if (typeof window !== 'undefined') window.localStorage?.setItem(VIDEO_AUDIO_PREF_KEY, newState ? 'muted' : 'unmuted');
    };

    const handleAddToCartClick = (e) => {
        e.preventDefault();
        e.stopPropagation();

        if (addedToCart) return;

        setAddedToCart(true);
        setTimeout(() => setAddedToCart(false), 2000);

        const cardImg = e.target.closest('.video-card-info')?.querySelector('.video-card-product-img');
        const targetIcon = document.getElementById('nav-cart-icon') || document.getElementById('mobile-cart-icon');

        if (cardImg && targetIcon) {
            const imgRect = cardImg.getBoundingClientRect();
            const targetRect = targetIcon.getBoundingClientRect();

            const clone = cardImg.cloneNode(true);
            clone.style.position = 'fixed';
            clone.style.zIndex = 9999;
            clone.style.top = `${imgRect.top}px`;
            clone.style.left = `${imgRect.left}px`;
            clone.style.width = `${imgRect.width}px`;
            clone.style.height = `${imgRect.height}px`;
            clone.style.borderRadius = '50%';
            clone.style.objectFit = 'cover';
            clone.style.transition = 'all 0.6s cubic-bezier(0.25, 1, 0.5, 1)';
            clone.style.pointerEvents = 'none';

            document.body.appendChild(clone);

            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    clone.style.top = `${targetRect.top + targetRect.height / 2 - 15}px`;
                    clone.style.left = `${targetRect.left + targetRect.width / 2 - 15}px`;
                    clone.style.width = '30px';
                    clone.style.height = '30px';
                    clone.style.opacity = '0.5';
                    clone.style.transform = 'scale(0.5)';
                });
            });

            setTimeout(() => {
                if (document.body.contains(clone)) document.body.removeChild(clone);
                targetIcon.classList.add('cart-bounce-anim');
                setTimeout(() => targetIcon.classList.remove('cart-bounce-anim'), 300);
                if (showToast) showToast('Added to cart successfully');
            }, 600);
        } else {
            if (showToast) showToast('Added to cart successfully');
        }

        onAddToCart?.({ id: video.productId, name: video.productName, price: video.productPrice, image: video.productImage });
    };

    return (
        <div className={`video-card video-card--${layout}`} ref={containerRef} style={{ background: '#ffffff', borderRadius: '20px', border: '1px solid rgba(0,0,0,0.08)', padding: '14px', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            <div className="video-card-thumb" onClick={togglePlay} style={{ position: 'relative', width: '100%', height: '360px', maxHeight: '380px', borderRadius: '14px', overflow: 'hidden', background: '#000000', cursor: 'pointer' }}>
                {visible ? (
                    <video
                        ref={videoRef}
                        src={isHlsStream && Hls.isSupported() ? undefined : videoSrc}
                        poster={video.thumbnail || derivedThumbnail || video.productImage || video.image}
                        playsInline
                        loop
                        autoPlay={visible}
                        defaultMuted={isMuted}
                        muted={isMuted}
                        preload="auto"
                        className="video-player-element"
                        style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                    />
                ) : (
                    <div className="skeleton skeleton-img" style={{ width: '100%', height: '100%' }} />
                )}

                {!isPlaying && visible && (
                    <div className="video-card-play"><Play size={32} fill="white" /></div>
                )}

                {visible && (
                    <button className="video-card-mute-btn" onClick={toggleMute} style={{ position: 'absolute', top: '10px', right: '10px', background: 'rgba(0,0,0,0.6)', border: 'none', borderRadius: '50%', padding: '8px', color: 'white', cursor: 'pointer', zIndex: 10 }}>
                        {isMuted ? <VolumeX size={16} /> : <Volume2 size={16} />}
                    </button>
                )}

                <Link
                    to={`/videos/${video.id}`}
                    onClick={(e) => e.stopPropagation()}
                    style={{
                        position: 'absolute',
                        top: '10px',
                        left: '10px',
                        background: 'rgba(0,0,0,0.7)',
                        border: '1px solid rgba(255,255,255,0.25)',
                        backdropFilter: 'blur(6px)',
                        borderRadius: '16px',
                        padding: '4px 10px',
                        color: '#FFFFFF',
                        fontSize: '11px',
                        fontWeight: '700',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px',
                        zIndex: 10,
                        textDecoration: 'none'
                    }}
                >
                    <span>📱</span>
                    <span>{isRTL() ? 'تيك توك' : 'Reels'}</span>
                </Link>

                <span className="video-card-duration">{video.duration || '0:00'}</span>
                <span className="video-card-views"><Eye size={12} /> {video.views}</span>
            </div>
            <div className="video-card-info" style={{ padding: '12px 2px 2px', display: 'flex', flexDirection: 'column', flex: 1 }}>
                <h4 className="video-card-title" style={{ fontSize: '15px', fontWeight: 700, color: '#111827', margin: '0 0 8px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{video.title}</h4>
                {video.productName && (
                    <Link to={`/product/${video.productId}`} className="video-card-product" style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 10px', borderRadius: '12px', background: '#f8fafc', border: '1px solid #e2e8f0', textDecoration: 'none', overflow: 'hidden' }}>
                        <img 
                            src={video.productImage} 
                            alt={video.productName} 
                            className="video-card-product-img" 
                            style={{ width: '48px', height: '48px', minWidth: '48px', maxWidth: '48px', objectFit: 'cover', borderRadius: '8px', border: '1px solid #e2e8f0', display: 'block' }} 
                        />
                        <div className="video-card-product-details" style={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0, overflow: 'hidden' }}>
                            <span className="video-card-product-name" style={{ fontSize: '12px', fontWeight: 600, color: '#111827', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {isRTL() ? (video.productNameAr || video.productName) : (video.productNameEn || video.productName)}
                            </span>
                            <span className="video-card-product-price" style={{ fontSize: '13px', fontWeight: 800, color: '#e02424', marginTop: '2px' }}>
                                ${video.productPrice?.toLocaleString()}
                            </span>
                        </div>
                    </Link>
                )}
                {video.productId && (
                    <div style={{ display: 'flex', gap: '8px', marginTop: '10px', flexWrap: 'wrap' }}>
                        <button
                            className="video-card-buy-btn"
                            onClick={handleAddToCartClick}
                            disabled={addedToCart}
                            style={{
                                background: addedToCart ? '#10b981' : '#111827',
                                color: '#ffffff',
                                border: '1px solid #111827',
                                borderRadius: '10px',
                                padding: '8px 12px',
                                fontSize: '12px',
                                fontWeight: '700',
                                cursor: 'pointer',
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '6px',
                                flex: 1
                            }}
                        >
                            {addedToCart ? (
                                <><span>✓</span> {t('ADD_TO_CART')}</>
                            ) : (
                                <><ShoppingBag size={14} /> {t('ADD_TO_CART')}</>
                            )}
                        </button>
                        <Link
                            to={`/product/${video.productId}`}
                            className="video-card-buy-btn"
                            onClick={(e) => e.stopPropagation()}
                            style={{ 
                                flex: 1, 
                                textAlign: 'center', 
                                display: 'flex', 
                                alignItems: 'center', 
                                justifyContent: 'center',
                                background: '#ffffff',
                                color: '#111827',
                                border: '1px solid #d1d5db',
                                borderRadius: '10px',
                                padding: '8px 12px',
                                fontSize: '12px',
                                fontWeight: '700',
                                textDecoration: 'none'
                            }}
                        >
                            {t('VIEW_ALL')}
                        </Link>
                    </div>
                )}
            </div>
        </div>
    );
};

export default VideoCard;
